import React, { useState, useEffect, useCallback } from 'react';
import api from '../config/api';
import './SyncLogs.css';
import { formatDateTime } from '../utils/dateUtils';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../utils/pagination';

interface SyncLogEntry {
  id: number;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  entity?: string;
  operation?: string;
  // The AIA id of the record the entry is about — the coding, for a codings sync.
  aia_record_id?: string | null;
  record_id?: number | null;
  details?: any;
}

interface SyncStats {
  total: number;
  byLevel: Record<string, number>;
  byEntity: Record<string, number>;
  oldest: string | null;
  newest: string | null;
}

const SyncLogs: React.FC = () => {
  const [logs, setLogs] = useState<SyncLogEntry[]>([]);
  const [stats, setStats] = useState<SyncStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity: '',
    level: '',
    operation: '',
    aiaRecordId: ''
  });

  // Paginated server-side: a sync writes one entry per changed record, so the journal
  // outgrows anything worth loading whole.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.entity) params.append('entity', filters.entity);
      if (filters.level) params.append('level', filters.level);
      if (filters.operation) params.append('operation', filters.operation);
      if (filters.aiaRecordId.trim())
        params.append('aiaRecordId', filters.aiaRecordId.trim());
      params.append('page', String(page));
      params.append('limit', String(pageSize));

      const response = await api.get(`/api/sync-logs?${params.toString()}`);
      setLogs(response.data.data);
      setTotal(response.data.pagination?.total ?? response.data.total ?? 0);
      setTotalPages(response.data.pagination?.totalPages ?? 1);
    } catch (error) {
      console.error('Error fetching sync logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/sync-logs/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching sync stats:', error);
    }
  }, []);

  // Declared after fetchLogs/fetchStats: naming them in the dependency array
  // requires them to be initialised, and the array is evaluated during render.
  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // Any filter change re-queries the whole journal, so the current page number is
  // meaningless against the new result set.
  const updateFilter = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearLogs = async () => {
    try {
      await api.delete('/api/sync-logs');
      setPage(1);
      await fetchLogs();
      await fetchStats();
    } catch (error) {
      console.error('Error clearing logs:', error);
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warn': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'success': return '#22c55e';
      case 'error': return '#ef4444';
      case 'warn': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return formatDateTime(timestamp);
  };

  if (loading) {
    return (
      <div className="sync-logs-page">
        <div className="container">
          <div className="page-loading">
            <div className="spinner"></div>
            <p>Chargement des journaux de synchronisation...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sync-logs-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Journaux de Synchronisation</h1>
            </div>
          </div>
        </div>

        {stats && (
          <div className="stats-section">
            <h2>Statistiques de Synchronisation</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Journaux</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.byLevel.success || 0}</div>
                <div className="stat-label">Succès</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.byLevel.error || 0}</div>
                <div className="stat-label">Erreurs</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.byLevel.warn || 0}</div>
                <div className="stat-label">Avertissements</div>
              </div>
            </div>
          </div>
        )}

        <div className="filters-section">
          <h2>Filtres</h2>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Entité</label>
              <select
                value={filters.entity}
                onChange={(e) => updateFilter('entity', e.target.value)}
              >
                <option value="">Toutes les Entités</option>
                <option value="SYNC">Synchronisation</option>
                <option value="SUPERVISORS">Superviseurs</option>
                <option value="CODERS">Codeurs</option>
                <option value="GAMES">Matchs</option>
                <option value="CODINGS">Codages</option>
                <option value="SUPERVISIONS">Supervisions</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Niveau</label>
              <select
                value={filters.level}
                onChange={(e) => updateFilter('level', e.target.value)}
              >
                <option value="">Tous les Niveaux</option>
                <option value="success">Succès</option>
                <option value="error">Erreur</option>
                <option value="warn">Avertissement</option>
                <option value="info">Info</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Opération</label>
              <select
                value={filters.operation}
                onChange={(e) => updateFilter('operation', e.target.value)}
              >
                <option value="">Toutes les Opérations</option>
                <option value="CREATE">Création</option>
                <option value="UPDATE">Mise à jour</option>
                <option value="DELETE">Suppression</option>
                <option value="CODER_LOCKED">Codeur non mis à jour</option>
                <option value="ERROR">Erreur</option>
                <option value="SUMMARY">Récapitulatif</option>
              </select>
            </div>
            <div className="filter-group">
              <label>ID AIA de l'enregistrement</label>
              <input
                type="text"
                className="filter-input"
                value={filters.aiaRecordId}
                onChange={(e) => updateFilter('aiaRecordId', e.target.value)}
                placeholder="ID du codage..."
              />
            </div>
            <div className="filter-group">
              <button className="btn-secondary" onClick={fetchLogs}>
                🔄 Actualiser
              </button>
              <button className="btn-danger" onClick={clearLogs}>
                🗑️ Effacer Journaux
              </button>
            </div>
          </div>
        </div>

        <div className="logs-section">
          {/* The total comes from the API, not from the rows on screen: only one page
              of the journal is loaded. */}
          <h2>Journaux de Synchronisation ({total})</h2>
          <div className="logs-container">
            {logs.length === 0 ? (
              <div className="no-logs">
                <p>Aucun journal de synchronisation trouvé</p>
              </div>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="log-entry">
                  <div className="log-header">
                    <div className="log-level" style={{ color: getLevelColor(log.level) }}>
                      {getLevelIcon(log.level)} {log.level.toUpperCase()}
                    </div>
                    <div className="log-timestamp">
                      {formatTimestamp(log.timestamp)}
                    </div>
                  </div>
                  <div className="log-content">
                    <div className="log-message">{log.message}</div>
                    {(log.entity || log.operation || log.aia_record_id) && (
                      <div className="log-meta">
                        {log.entity && <span className="log-entity">[{log.entity}]</span>}
                        {log.operation && <span className="log-operation">[{log.operation}]</span>}
                        {log.aia_record_id && (
                          <span className="log-record">{log.aia_record_id}</span>
                        )}
                      </div>
                    )}
                    {log.details && (
                      <div className="log-details">
                        <pre>{JSON.stringify(log.details, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {total > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                <span>
                  Affichage de {(page - 1) * pageSize + 1} à{' '}
                  {Math.min(page * pageSize, total)} sur {total} entrées
                </span>
                <span>
                  Éléments par page{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  ««
                </button>
                <button
                  className="pagination-btn"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  ‹
                </button>
                <span className="pagination-current">
                  Page {page} sur {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                >
                  ›
                </button>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncLogs;
